/**
 * Authorized Signatory Form Component
 * Third step of enhanced manufacturer registration
 */

import React, { useState, useEffect } from 'react';
import { Button, Input } from '../ui';
import { AuthorizedSignatory } from '../../services/registration.service';

interface AuthorizedSignatoryFormProps {
  data?: AuthorizedSignatory;
  onSave: (data: AuthorizedSignatory) => Promise<void>;
  onNext: () => void;
  onPrevious: () => void;
  saving: boolean;
}

export function AuthorizedSignatoryForm({ data, onSave, onNext, onPrevious, saving }: AuthorizedSignatoryFormProps) {
  const [formData, setFormData] = useState<AuthorizedSignatory>({
    fullName: '',
    designation: '',
    email: '',
    mobileNumber: '',
    aadhaarMasked: '',
    passportNumber: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.designation.trim()) {
      newErrors.designation = 'Designation is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Invalid mobile number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSave(formData);
      onNext();
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Full Name */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Name *
          </label>
          <Input
            value={formData.fullName}
            onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
            placeholder="Enter full name of authorized signatory"
            error={errors.fullName}
          />
        </div>

        {/* Designation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Designation *
          </label>
          <Input
            value={formData.designation}
            onChange={(e) => setFormData(prev => ({ ...prev, designation: e.target.value }))}
            placeholder="e.g., Director, Partner, Proprietor"
            error={errors.designation}
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="signatory@company.com"
            error={errors.email}
          />
        </div>

        {/* Mobile Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mobile Number *
          </label>
          <Input
            value={formData.mobileNumber}
            onChange={(e) => setFormData(prev => ({ ...prev, mobileNumber: e.target.value }))}
            placeholder="10-digit mobile number"
            maxLength={10}
            error={errors.mobileNumber}
          />
        </div>

        {/* Aadhaar (Masked) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Aadhaar Number (Last 4 digits)
            <span className="text-gray-500 text-xs ml-1">(Optional)</span>
          </label>
          <Input
            value={formData.aadhaarMasked || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, aadhaarMasked: e.target.value }))}
            placeholder="XXXX-XXXX-1234"
            maxLength={4}
          />
        </div>

        {/* Passport Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Passport Number
            <span className="text-gray-500 text-xs ml-1">(Optional)</span>
          </label>
          <Input
            value={formData.passportNumber || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, passportNumber: e.target.value }))}
            placeholder="Passport number"
          />
        </div>
      </div>

      {/* Information Box */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Privacy Notice</h3>
            <div className="text-sm text-yellow-700 mt-1">
              <p>• We only store masked Aadhaar information (last 4 digits) for verification purposes</p>
              <p>• Complete Aadhaar numbers are never stored in compliance with DPDP Act 2023</p>
              <p>• This person will be legally responsible for all manufacturer operations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={onPrevious}
        >
          Previous
        </Button>
        <Button
          type="submit"
          disabled={saving}
          className="px-6 py-2"
        >
          {saving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </div>
    </form>
  );
}